// The Plaid (bank account) add-account flow, ported from platform-app's
// AddPlaidAccount widget. The Plaid link_token is already minted by the backend and embedded
// in the scoped token, so we open Plaid Link immediately (no extra round trip) and, on
// success, submit the public_token + account id to the v2 API using the scoped session token.
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePlaidLink, type PlaidLinkOnExit, type PlaidLinkOnSuccess } from 'react-plaid-link';

import type { EnvironmentId } from '@/config/environments';
import { submitPlaidInternalAccountWithSession } from '@/api/payouts';
import { ApiError } from '@/api/client';
import type { InternalAccountLinkClaims } from '@/lib/sessionToken';
import { notifyParent } from '@/lib/embedMessages';

import { Banner } from '@/components/ds';

type Status = 'ready' | 'opening' | 'submitting' | 'done' | 'error';

type Props = {
  env: EnvironmentId;
  token: string;
  claims: InternalAccountLinkClaims;
};

export function AddPlaidAccount({ env, token, claims }: Props) {
  const linkToken = claims.plaid?.linkToken;
  if (!linkToken) {
    return <Banner kind="critical">This link can’t start the bank connection. Ask your provider to send you a new one.</Banner>;
  }
  return <PlaidRunner env={env} token={token} entityId={claims.entityId} publishableKey={claims.partnerName} linkToken={linkToken} />;
}

function PlaidRunner({
  env,
  token,
  entityId,
  publishableKey,
  linkToken,
}: {
  env: EnvironmentId;
  token: string;
  entityId: string;
  publishableKey: string;
  linkToken: string;
}) {
  const [status, setStatus] = useState<Status>('ready');
  const [error, setError] = useState('');

  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    async (publicToken, metadata) => {
      const account = metadata.accounts[0];
      if (!account) {
        const message = 'No bank account was selected. Please try again.';
        setStatus('error');
        setError(message);
        notifyParent({ flow: 'add', kind: 'plaid', status: 'error', message });
        return;
      }
      setStatus('submitting');
      setError('');
      try {
        const internalAccount = await submitPlaidInternalAccountWithSession(env, token, entityId, publishableKey, {
          plaidToken: publicToken,
          plaidAccountId: account.id,
        });
        setStatus('done');
        notifyParent({ flow: 'add', kind: 'plaid', status: 'success', accountId: internalAccount.id });
      } catch (ex) {
        const message = ex instanceof ApiError ? ex.message : 'We couldn’t save your bank account. Please try again.';
        setStatus('error');
        setError(message);
        notifyParent({ flow: 'add', kind: 'plaid', status: 'error', message });
      }
    },
    [env, token, entityId, publishableKey]
  );

  const onExit = useCallback<PlaidLinkOnExit>((err) => {
    // A plain user-cancel leaves no error — just return to the ready state.
    setStatus('ready');
    if (err) {
      const message = err.display_message || err.error_message || 'The bank connection was interrupted.';
      setError(message);
      notifyParent({ flow: 'add', kind: 'plaid', status: 'error', message });
    } else {
      notifyParent({ flow: 'add', kind: 'plaid', status: 'cancelled' });
    }
  }, []);

  const { open, ready } = usePlaidLink({ token: linkToken, onSuccess, onExit });

  const openPlaid = useCallback(() => {
    setStatus('opening');
    setError('');
    open();
  }, [open]);

  // This page is a single-purpose embed reached only via a one-time link, so there's no button —
  // open Plaid the moment it's ready. If the user backs out via onExit we notify the parent
  // (below) and leave it to them to reload the iframe with a fresh link rather than looping.
  const hasAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (ready && !hasAutoOpenedRef.current) {
      hasAutoOpenedRef.current = true;
      openPlaid();
    }
  }, [ready, openPlaid]);

  // If Plaid Link finishes (success path handled in onSuccess) the 'opening' state is replaced;
  // if it closes via onExit we go back to 'ready'. Nothing to do here on mount.
  useEffect(() => {
    if (status === 'opening' && !ready) setStatus('ready');
  }, [ready, status]);

  // Nothing to render except on error — Plaid's own overlay is the only UI here, and on
  // success it already closed itself (the parent was notified via postMessage).
  if (status !== 'error') return null;

  return (
    <Banner kind="critical" style={{ textAlign: 'left' }}>
      {error}
    </Banner>
  );
}
